import { useCallback, useEffect, useRef, useState } from 'react';
import useApiRequest from '../../../shared/hooks/useApiRequest';
import {
  generateAiOfferRecommendations,
  getAiOfferRules,
  listAiOfferRecommendations,
  normalizeAiOfferRecommendationError,
  publishAiOfferRecommendation,
  recordAiOfferRecommendationFeedback,
  saveAiOfferRules,
  updateAiOfferRecommendationStatus
} from '../api/aiOfferRecommendationsApi';

const DEFAULT_ERROR_MESSAGE = 'Unable to complete AI offer request.';
const GENERATION_POLL_INTERVAL_MS = 3000;

const useAiOfferRecommendations = ({ request, storeToken, onToast } = {}) => {
  const {
    execute: executeListRequest,
    loading: listLoading,
    error: listError
  } = useApiRequest();
  const {
    execute: executeMutation,
    loading: mutationLoading
  } = useApiRequest();
  const {
    execute: executeRulesRequest,
    loading: rulesLoading
  } = useApiRequest();

  const [rules, setRules] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [hasLoadedRecommendations, setHasLoadedRecommendations] = useState(false);
  const [lastRateLimitMessage, setLastRateLimitMessage] = useState(null);
  const [generationQuota, setGenerationQuota] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generationMessage, setGenerationMessage] = useState(null);
  const generationInFlightRef = useRef(null);
  const previousGenerationStatusRef = useRef(null);
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;

  const sameRecommendationId = useCallback((left, right) => String(left) === String(right), []);

  const resolveError = useCallback((error, fallbackMessage = DEFAULT_ERROR_MESSAGE) => (
    normalizeAiOfferRecommendationError(error, fallbackMessage)
  ), []);

  const setRateLimitFromError = useCallback((errorDetails) => {
    setLastRateLimitMessage(errorDetails?.isRateLimited ? errorDetails.message : null);
    if (errorDetails?.isRateLimited) {
      setGenerationQuota((previous) => ({
        limit: previous?.limit ?? null,
        remaining: 0,
        retryAtMs: errorDetails.retryAfterSeconds
          ? Date.now() + errorDetails.retryAfterSeconds * 1000
          : previous?.retryAtMs ?? null
      }));
    }
  }, []);

  const upsertRecommendation = useCallback((nextRecommendation) => {
    setRecommendations((previous) => {
      const existingIndex = previous.findIndex((item) => sameRecommendationId(item.id, nextRecommendation.id));
      if (existingIndex < 0) {
        return [nextRecommendation, ...previous];
      }
      return previous.map((item, index) => (
        index === existingIndex ? { ...item, ...nextRecommendation } : item
      ));
    });
  }, [sameRecommendationId]);

  const applyRecommendationEnvelope = useCallback((envelope = {}, { toastOnTransition = true } = {}) => {
    const nextRecommendations = envelope.recommendations || [];
    const nextStatus = envelope.generationStatus || null;
    const nextMessage = envelope.generationMessage || null;
    const previousStatus = previousGenerationStatusRef.current;

    setRecommendations(nextRecommendations);
    setHasLoadedRecommendations(true);
    setGenerationStatus(nextStatus);
    setGenerationMessage(nextMessage);
    setIsGenerating(nextStatus === 'IN_PROGRESS');
    previousGenerationStatusRef.current = nextStatus;

    if (envelope.generationLimit != null) {
      setGenerationQuota({
        limit: envelope.generationLimit,
        remaining: envelope.generationsRemaining ?? null,
        retryAtMs: envelope.generationRetryAfterSeconds
          ? Date.now() + envelope.generationRetryAfterSeconds * 1000
          : null
      });
    }

    if (toastOnTransition && previousStatus === 'IN_PROGRESS' && nextStatus !== 'IN_PROGRESS') {
      if (nextStatus === 'FAILED') {
        onToastRef.current?.({ message: nextMessage || 'Unable to generate AI offer drafts.', variant: 'error' });
      } else if (nextRecommendations.length > 0) {
        onToastRef.current?.({ message: 'AI offer drafts are ready.', variant: 'success' });
      }
    }

    return nextRecommendations;
  }, []);

  const refetchRecommendations = useCallback(async ({ status } = {}) => {
    if (typeof request !== 'function' || !storeToken) {
      setRecommendations([]);
      setHasLoadedRecommendations(true);
      return [];
    }

    return executeListRequest(async () => (
      listAiOfferRecommendations(request, { status })
    ), {
      logModule: 'useAiOfferRecommendations.list',
      onSuccess: (envelope) => {
        applyRecommendationEnvelope(envelope);
        setLastRateLimitMessage(null);
      },
      onError: (error) => {
        setRecommendations([]);
        setHasLoadedRecommendations(true);
        const errorDetails = resolveError(error, 'Unable to load AI offer drafts.');
        setRateLimitFromError(errorDetails);
        onToastRef.current?.({ message: errorDetails.message, variant: 'error' });
      }
    });
  }, [applyRecommendationEnvelope, executeListRequest, request, resolveError, setRateLimitFromError, storeToken]);

  useEffect(() => {
    refetchRecommendations().catch(() => null);
  }, [refetchRecommendations]);

  useEffect(() => {
    if (generationStatus !== 'IN_PROGRESS') {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      refetchRecommendations().catch(() => null);
    }, GENERATION_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [generationStatus, refetchRecommendations]);

  const fetchRules = useCallback(async () => {
    if (typeof request !== 'function' || !storeToken) {
      return null;
    }
    return executeRulesRequest(async () => getAiOfferRules(request), {
      logModule: 'useAiOfferRecommendations.rules.get',
      onSuccess: (nextRules) => setRules(nextRules),
      onError: () => {
        // Rules fall back to backend defaults on the next open; no toast needed.
      }
    });
  }, [executeRulesRequest, request, storeToken]);

  const saveRules = useCallback(async (payload = {}) => (
    executeRulesRequest(async () => {
      if (typeof request !== 'function' || !storeToken) {
        throw new Error('Missing authenticated request context');
      }
      return saveAiOfferRules(request, payload);
    }, {
      logModule: 'useAiOfferRecommendations.rules.save',
      onSuccess: (nextRules) => {
        setRules(nextRules);
        onToastRef.current?.({ message: 'AI offer rules saved.', variant: 'success' });
      },
      onError: (error) => {
        const errorDetails = resolveError(error, 'Unable to save AI offer rules.');
        onToastRef.current?.({ message: errorDetails.message, variant: 'error' });
      }
    })
  ), [executeRulesRequest, request, resolveError, storeToken]);

  const generateRecommendations = useCallback(async (payload = {}) => {
    if (generationInFlightRef.current) {
      return generationInFlightRef.current;
    }

    const generationPromise = (async () => {
      setIsGenerating(true);
      try {
        return await executeMutation(async () => {
          if (typeof request !== 'function' || !storeToken) {
            throw new Error('Missing authenticated request context');
          }

          const envelope = await generateAiOfferRecommendations(request, payload);
          applyRecommendationEnvelope(envelope, { toastOnTransition: false });
          return envelope.recommendations || [];
        }, {
          logModule: 'useAiOfferRecommendations.generate',
          onSuccess: (nextRecommendations) => {
            setLastRateLimitMessage(null);
            if (previousGenerationStatusRef.current === 'IN_PROGRESS' || nextRecommendations?.length === 0) {
              onToastRef.current?.({ message: 'AI offer generation started.', variant: 'success' });
            } else {
              onToastRef.current?.({ message: 'AI offer drafts generated.', variant: 'success' });
            }
          },
          onError: (error) => {
            const errorDetails = resolveError(error, 'Unable to generate AI offer drafts.');
            setRateLimitFromError(errorDetails);
            onToastRef.current?.({ message: errorDetails.message, variant: 'error' });
          }
        });
      } finally {
        if (previousGenerationStatusRef.current !== 'IN_PROGRESS') {
          setIsGenerating(false);
        }
        generationInFlightRef.current = null;
      }
    })();

    generationInFlightRef.current = generationPromise;
    return generationPromise;
  }, [applyRecommendationEnvelope, executeMutation, request, resolveError, setRateLimitFromError, storeToken]);

  const updateRecommendationStatus = async (recommendationId, payload = {}) => executeMutation(async () => {
    if (typeof request !== 'function' || !storeToken) {
      throw new Error('Missing authenticated request context');
    }

    const updatedRecommendation = await updateAiOfferRecommendationStatus(request, recommendationId, payload);
    upsertRecommendation(updatedRecommendation);
    return updatedRecommendation;
  }, {
    logModule: 'useAiOfferRecommendations.status',
    onSuccess: () => setLastRateLimitMessage(null),
    onError: (error) => {
      const errorDetails = resolveError(error, 'Unable to update AI offer draft.');
      setRateLimitFromError(errorDetails);
      onToastRef.current?.({ message: errorDetails.message, variant: 'error' });
    }
  });

  const recordFeedback = async (recommendationId, payload = {}) => executeMutation(async () => {
    if (typeof request !== 'function' || !storeToken) {
      throw new Error('Missing authenticated request context');
    }

    const updatedRecommendation = await recordAiOfferRecommendationFeedback(request, recommendationId, payload);
    upsertRecommendation(updatedRecommendation);
    return updatedRecommendation;
  }, {
    logModule: 'useAiOfferRecommendations.feedback',
    onSuccess: () => setLastRateLimitMessage(null),
    onError: (error) => {
      const errorDetails = resolveError(error, 'Unable to record AI offer feedback.');
      setRateLimitFromError(errorDetails);
      onToastRef.current?.({ message: errorDetails.message, variant: 'error' });
    }
  });

  const publishRecommendation = async (recommendationId, payload = {}) => executeMutation(async () => {
    if (typeof request !== 'function' || !storeToken) {
      throw new Error('Missing authenticated request context');
    }

    const publishedRecommendation = await publishAiOfferRecommendation(request, recommendationId, payload);
    upsertRecommendation(publishedRecommendation);
    return publishedRecommendation;
  }, {
    logModule: 'useAiOfferRecommendations.publish',
    onSuccess: () => {
      setLastRateLimitMessage(null);
      onToastRef.current?.({ message: 'AI offer published.', variant: 'success' });
    },
    onError: (error) => {
      const errorDetails = resolveError(error, 'Unable to publish AI offer.');
      setRateLimitFromError(errorDetails);
      // A "similar offer already exists" soft duplicate is surfaced by the panel as a
      // confirm dialog ("publish anyway?"), not a toast — skip toasting so it isn't shown twice.
      if (errorDetails.code === 'OFFER_DUPLICATE_SIMILAR') {
        return;
      }
      onToastRef.current?.({ message: errorDetails.message, variant: 'error' });
    }
  });

  const acceptRecommendation = (recommendationId, reason) => (
    updateRecommendationStatus(recommendationId, { status: 'ACCEPTED', reason })
  );

  const rejectRecommendation = (recommendationId, reason) => (
    updateRecommendationStatus(recommendationId, { status: 'REJECTED', reason })
  );

  return {
    recommendations,
    rules,
    rulesLoading,
    fetchRules,
    saveRules,
    refetchRecommendations,
    generateRecommendations,
    updateRecommendationStatus,
    acceptRecommendation,
    rejectRecommendation,
    recordFeedback,
    publishRecommendation,
    hasLoadedRecommendations,
    lastRateLimitMessage,
    generationQuota,
    generationStatus,
    generationMessage,
    isGenerating,
    isMutating: mutationLoading,
    isInitialLoading: listLoading && !hasLoadedRecommendations,
    loading: listLoading || mutationLoading,
    error: listError
  };
};

export default useAiOfferRecommendations;
